// atlas/coords.ts
//
// Coordinate maps for the hair/makeup analysis "atlas" images. The backend
// generates one packed atlas (1536×2048 WebP) per scan whose cells are laid out
// to the magenta-bordered blank template; clients crop each cell by these fixed
// rects. Keep these in sync with the backend canonical files:
//   apps/backend/code/static/templates/hair_analysis_template_blank_coords.json
//   apps/backend/code/static/templates/makeup_analysis_template_blank_coords.json
// The result_json.atlas_version ("hair-v1" / "makeup-v1") selects the map.

export interface CellRect { x: number; y: number; w: number; h: number }
export interface AtlasCoords {
  canvas: { w: number; h: number };
  border_px: number;
  cells: Record<string, CellRect>;
}

/**
 * Extra inner inset (in canvas px, per edge) trimmed from every cell on top of
 * border_px. The magenta cut-guide line is ~border_px thick in the template, but
 * the generated atlas can render it slightly thicker / shifted (the model paints
 * at 2K and we normalize to the 1536×2048 canvas), so trimming only border_px can
 * leave a magenta sliver. This buffer crops a touch further inside so the line
 * never shows. Kept small so the face cells lose negligible content.
 */
export const CELL_INSET_PX = 8;

/** Total trim per edge for a cell: the cut-guide border plus the safety inset. */
export function cellTrim(coords: AtlasCoords): number {
  return coords.border_px + CELL_INSET_PX;
}

export const hairCoords: AtlasCoords = {
  canvas: { w: 1536, h: 2048 },
  border_px: 3,
  cells: {
    A1: { x: 40, y: 40, w: 346, h: 430 },
    A2: { x: 410, y: 40, w: 346, h: 430 },
    A3: { x: 780, y: 40, w: 346, h: 430 },
    A4: { x: 1150, y: 40, w: 346, h: 430 },
    B1: { x: 40, y: 510, w: 272, h: 250 },
    B2: { x: 336, y: 510, w: 272, h: 250 },
    B3: { x: 632, y: 510, w: 272, h: 250 },
    B4: { x: 928, y: 510, w: 272, h: 250 },
    B5: { x: 1224, y: 510, w: 272, h: 250 },
    B6: { x: 40, y: 784, w: 346, h: 250 },
    B7: { x: 410, y: 784, w: 346, h: 250 },
    B8: { x: 780, y: 784, w: 346, h: 250 },
    B9: { x: 1150, y: 784, w: 346, h: 250 },
    C1: { x: 40, y: 1074, w: 346, h: 300 },
    C2: { x: 410, y: 1074, w: 346, h: 300 },
    C3: { x: 780, y: 1074, w: 346, h: 300 },
    C4: { x: 1150, y: 1074, w: 346, h: 300 },
    D1: { x: 40, y: 1414, w: 223, h: 90 },
    D2: { x: 287, y: 1414, w: 223, h: 90 },
    D3: { x: 533, y: 1414, w: 223, h: 90 },
    D4: { x: 780, y: 1414, w: 223, h: 90 },
    D5: { x: 1027, y: 1414, w: 223, h: 90 },
    D6: { x: 1273, y: 1414, w: 223, h: 90 },
    D7: { x: 40, y: 1528, w: 187, h: 90 },
    D8: { x: 251, y: 1528, w: 187, h: 90 },
    D9: { x: 463, y: 1528, w: 187, h: 90 },
    D10: { x: 674, y: 1528, w: 187, h: 90 },
    D11: { x: 886, y: 1528, w: 187, h: 90 },
    D12: { x: 1097, y: 1528, w: 187, h: 90 },
    D13: { x: 1309, y: 1528, w: 187, h: 90 },
  },
};

// Makeup atlas — face cells (A1 hero, C1–C10 full-face looks) are PORTRAIT so a
// head-and-shoulders selfie frames on the face, not the torso. Eyes (B*) and the
// real-lip crops (D19–D23) stay wide on purpose. Keep in sync with
// apps/backend/code/static/templates/makeup_analysis_template_blank_coords.json.
export const makeupCoords: AtlasCoords = {
  canvas: { w: 1536, h: 2048 },
  border_px: 3,
  cells: {
    A1: { x: 40, y: 40, w: 400, h: 430 },
    B1: { x: 40, y: 494, w: 272, h: 150 },
    B2: { x: 336, y: 494, w: 272, h: 150 },
    B3: { x: 632, y: 494, w: 272, h: 150 },
    B4: { x: 928, y: 494, w: 272, h: 150 },
    B5: { x: 1224, y: 494, w: 272, h: 150 },
    B6: { x: 40, y: 668, w: 346, h: 150 },
    B7: { x: 410, y: 668, w: 346, h: 150 },
    B8: { x: 780, y: 668, w: 346, h: 150 },
    B9: { x: 1150, y: 668, w: 346, h: 150 },
    C1: { x: 40, y: 854, w: 272, h: 320 },
    C2: { x: 336, y: 854, w: 272, h: 320 },
    C3: { x: 632, y: 854, w: 272, h: 320 },
    C4: { x: 928, y: 854, w: 272, h: 320 },
    C5: { x: 1224, y: 854, w: 272, h: 320 },
    C6: { x: 40, y: 1198, w: 272, h: 320 },
    C7: { x: 336, y: 1198, w: 272, h: 320 },
    C8: { x: 632, y: 1198, w: 272, h: 320 },
    C9: { x: 928, y: 1198, w: 272, h: 320 },
    C10: { x: 1224, y: 1198, w: 272, h: 320 },
    D1: { x: 40, y: 1554, w: 161, h: 72 },
    D2: { x: 225, y: 1554, w: 161, h: 72 },
    D3: { x: 410, y: 1554, w: 161, h: 72 },
    D4: { x: 595, y: 1554, w: 161, h: 72 },
    D5: { x: 780, y: 1554, w: 161, h: 72 },
    D6: { x: 965, y: 1554, w: 161, h: 72 },
    D7: { x: 1150, y: 1554, w: 161, h: 72 },
    D8: { x: 1335, y: 1554, w: 161, h: 72 },
    D9: { x: 40, y: 1650, w: 161, h: 72 },
    D10: { x: 225, y: 1650, w: 161, h: 72 },
    D11: { x: 410, y: 1650, w: 161, h: 72 },
    D12: { x: 595, y: 1650, w: 161, h: 72 },
    D13: { x: 780, y: 1650, w: 161, h: 72 },
    D14: { x: 965, y: 1650, w: 161, h: 72 },
    D15: { x: 1150, y: 1650, w: 161, h: 72 },
    D16: { x: 1335, y: 1650, w: 161, h: 72 },
    D17: { x: 40, y: 1746, w: 187, h: 72 },
    D18: { x: 251, y: 1746, w: 187, h: 72 },
    D19: { x: 463, y: 1746, w: 187, h: 72 },
    D20: { x: 674, y: 1746, w: 187, h: 72 },
    D21: { x: 886, y: 1746, w: 187, h: 72 },
    D22: { x: 1097, y: 1746, w: 187, h: 72 },
    D23: { x: 1309, y: 1746, w: 187, h: 72 },
    E1: { x: 40, y: 1850, w: 223, h: 72 },
    E2: { x: 287, y: 1850, w: 223, h: 72 },
    E3: { x: 533, y: 1850, w: 223, h: 72 },
    E4: { x: 780, y: 1850, w: 223, h: 72 },
    E5: { x: 1027, y: 1850, w: 223, h: 72 },
    E6: { x: 1273, y: 1850, w: 223, h: 72 },
    E7: { x: 40, y: 1946, w: 272, h: 72 },
    E8: { x: 336, y: 1946, w: 272, h: 72 },
    E9: { x: 632, y: 1946, w: 272, h: 72 },
    E10: { x: 928, y: 1946, w: 272, h: 72 },
    E11: { x: 1224, y: 1946, w: 272, h: 72 },
  },
};

export const atlasByVersion: Record<string, AtlasCoords> = {
  'hair-v1': hairCoords,
  'makeup-v1': makeupCoords,
};

/**
 * Build CSS background props that crop a single atlas cell into a responsive box.
 * The host element should set its own aspect-ratio to cell.w / cell.h (see
 * AtlasCell). border_px is trimmed off each edge to drop the magenta cut guide.
 */
export function atlasCellStyle(atlasUrl: string, coords: AtlasCoords, cellId: string): React.CSSProperties | null {
  if (!(cellId in coords.cells)) return null;
  const cell = coords.cells[cellId];
  const b = cellTrim(coords);
  const x = cell.x + b;
  const y = cell.y + b;
  const w = cell.w - 2 * b;
  const h = cell.h - 2 * b;
  const { w: cw, h: ch } = coords.canvas;
  const posX = cw - w <= 0 ? 0 : (x / (cw - w)) * 100;
  const posY = ch - h <= 0 ? 0 : (y / (ch - h)) * 100;
  return {
    backgroundImage: `url(${atlasUrl})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${(cw / w) * 100}% ${(ch / h) * 100}%`,
    backgroundPosition: `${posX}% ${posY}%`,
  };
}
